import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Select,
  Button,
  Card,
  Row,
  Col,
  Alert,
  Typography,
  Tooltip,
  Badge,
  Table,
} from "antd";
import {
  InfoCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { StepOneData } from "./StepOne";
import { DMA_LIST } from "../../../../constants/DMAList";
import { REGIONS_LIST } from "../../../../constants/RegionsList";
import { LOCALE_LIST } from "../../../../constants/LocaleList";
import Papa from "papaparse";
import { cloneDeep } from "lodash";

const { Title, Text, Paragraph } = Typography;

interface StepTwoProps {
  stepOneData: StepOneData;
  onNext: (data: StepTwoData) => void;
  onPrevious: () => void;
  onNameChange?: (name: string) => void;
}

export interface StepTwoData {
  name: string;
  campaignObjective: string;
  userId: string;
  targetVariable: string;
  age?: string;
  gender?: string;
  locale?: string;
  geoLocationType?: string;
  geoLocation?: string;
  devicePlatform?: string;
  userOs?: string;
  userDevice?: string;
}

const StepTwo: React.FC<StepTwoProps> = ({
  stepOneData,
  onNext,
  onPrevious,
  onNameChange,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [validationWarnings, setValidationWarnings] = useState<string[]>([]);
  const [selectedColumns, setSelectedColumns] = useState<
    Record<string, string>
  >({});

  const { headers, dataFile, previewData } = stepOneData;

  const campaignObjectiveOptions = [
    { label: "Sales", value: "sales" },
    { label: "Lead Generation", value: "lead" },
    { label: "Reach", value: "reach" },
  ];

  const geoLocationTypeOptions = [
    { label: "Designated Market Area", value: "dma" },
    { label: "Regions", value: "region" },
  ];

  // Find matching headers for preselection
  const findMatchingHeader = (searchTerms: string[]): string | undefined => {
    // Try exact match first (case insensitive)
    for (const term of searchTerms) {
      const exactMatch = headers.find(
        (header) => header.toLowerCase() === term.toLowerCase()
      );
      if (exactMatch) return exactMatch;
    }

    // Then try contains match (case insensitive)
    for (const term of searchTerms) {
      const containsMatch = headers.find((header) =>
        header.toLowerCase().includes(term.toLowerCase())
      );
      if (containsMatch) return containsMatch;
    }

    // Finally try word match (check if any word in header matches search term)
    for (const term of searchTerms) {
      const wordMatch = headers.find((header) => {
        const headerWords = header.toLowerCase().split(/\s+|_|-/);
        const termWords = term.toLowerCase().split(/\s+|_|-/);
        return termWords.some(word => headerWords.includes(word));
      });
      if (wordMatch) return wordMatch;
    }

    return undefined;
  };

  // Set initial values for form fields based on header names
  useEffect(() => {
    // Define search terms for each field
    const userId = findMatchingHeader(["user id", "userid", "user_id", "id", "customer id", "customer_id", "customerid", "email"]);
    const targetVariable = findMatchingHeader(["target", "target variable", "target_variable", "sales", "revenue", "conversion"]);
    const age = findMatchingHeader(["age", "age range", "age_range", "agerange"]);
    const gender = findMatchingHeader(["gender", "sex"]);
    const locale = findMatchingHeader(["locale", "language", "country"]);
    const geoLocation = findMatchingHeader(["dma", "region", "location", "geo", "geo location", "geo_location", "geolocation"]);
    const devicePlatform = findMatchingHeader(["device platform", "device_platform", "deviceplatform", "platform", "device type"]);
    const userOs = findMatchingHeader(["user os", "user_os", "useros", "os", "operating system", "system"]);
    const userDevice = findMatchingHeader(["user device", "user_device", "userdevice", "device", "device name", "device_name"]);

    // Determine geo location type based on the matched header
    let geoLocationType: string | undefined = undefined;
    if (geoLocation) {
      const lowerGeoLocation = geoLocation.toLowerCase();
      if (lowerGeoLocation.includes("dma")) {
        geoLocationType = "dma";
      } else if (lowerGeoLocation.includes("region")) {
        geoLocationType = "region";
      } else {
        // Default to DMA as requested
        geoLocationType = "dma";
      }
    }

    // Only set values that exist in headers
    const initialValues: Partial<StepTwoData> = {
      campaignObjective: "lead", // Default value
      geoLocationType: geoLocationType,
    };

    if (userId) initialValues.userId = userId;
    if (targetVariable) initialValues.targetVariable = targetVariable;
    if (age) initialValues.age = age;
    if (gender) initialValues.gender = gender;
    if (locale) initialValues.locale = locale;
    if (geoLocation) initialValues.geoLocation = geoLocation;
    if (devicePlatform) initialValues.devicePlatform = devicePlatform;
    if (userOs) initialValues.userOs = userOs;
    if (userDevice) initialValues.userDevice = userDevice;

    // Log the preselected values for debugging
    console.log("Preselected values:", initialValues);
    console.log("Available headers:", headers);

    form.setFieldsValue(initialValues);
  }, [headers, form]);

  // Track selected columns to highlight them in the preview table
  useEffect(() => {
    const values = form.getFieldsValue();
    const selected: Record<string, string> = {};

    Object.entries(values).forEach(([key, value]) => {
      if (value && headers.includes(value as string)) {
        selected[value as string] = key;
      }
    });

    setSelectedColumns(selected);

    // We don't include form.getFieldsValue() in the dependency array as it would cause infinite re-renders
    // Instead, we use the Form's onValuesChange to update selected columns when values change
  }, [form, headers]);

  const validateAge = (results: any, age: string): boolean => {
    const clonedResult = cloneDeep(results);
    const index = clonedResult.data[0].indexOf(age);
    clonedResult.data.shift();

    // Regular expression to match the "age1-age2" or "age1+" format
    const ageRangeRegex = /^(\d+-\d+|\d+\+|default)$/;

    for (const element of clonedResult.data) {
      const valueToValidate = element[index];
      if (!ageRangeRegex.test(valueToValidate)) {
        setValidationError(`Unsupported age value: ${valueToValidate}`);
        return false;
      }
    }

    return true;
  };

  const validateGender = (results: any, gender: string): boolean => {
    const clonedResult = cloneDeep(results);
    const index = clonedResult.data[0].indexOf(gender);
    clonedResult.data.shift();

    // Regular expression to match gender values
    const genderRegex = /^(male|female|default)$/;

    for (const element of clonedResult.data) {
      const valueToValidate = element[index].toLowerCase();
      if (!genderRegex.test(valueToValidate)) {
        setValidationError(`Unsupported gender value: ${valueToValidate}`);
        return false;
      }
    }

    return true;
  };

  const validateLocale = (results: any, locale: string): boolean => {
    const clonedResult = cloneDeep(results);
    const index = clonedResult.data[0].indexOf(locale);
    clonedResult.data.shift();

    for (const element of clonedResult.data) {
      if (!LOCALE_LIST.map((item) => item.name).includes(element[index])) {
        setValidationError(`Unsupported locale value: ${element[index]}`);
        return false;
      }
    }

    return true;
  };

  const validateDevicePlatform = (
    results: any,
    devicePlatform: string
  ): boolean => {
    const clonedResult = cloneDeep(results);
    const index = clonedResult.data[0].indexOf(devicePlatform);
    clonedResult.data.shift();

    // Regular expression to match device platform values
    const devicePlatformRegex = /^(mobile|desktop)$/;

    for (const element of clonedResult.data) {
      const valueToValidate = element[index].toLowerCase();
      if (!devicePlatformRegex.test(valueToValidate)) {
        setValidationError(
          `Unsupported device platform value: ${valueToValidate}`
        );
        return false;
      }
    }

    return true;
  };

  const validateUserOs = (results: any, userOs: string): boolean => {
    const clonedResult = cloneDeep(results);
    const index = clonedResult.data[0].indexOf(userOs);
    clonedResult.data.shift();

    // Regular expression to match user OS values
    const userOsRegex = /^(Android|Windows|iOS|Windows Phone|default)$/;

    for (const element of clonedResult.data) {
      const valueToValidate = element[index];
      if (!userOsRegex.test(valueToValidate)) {
        setValidationError(`Unsupported user OS value: ${valueToValidate}`);
        return false;
      }
    }

    return true;
  };

  const validateUserDevice = (results: any, userDevice: string): boolean => {
    const clonedResult = cloneDeep(results);
    const index = clonedResult.data[0].indexOf(userDevice);
    clonedResult.data.shift();

    // Regular expression to match user device values
    const userDeviceRegex = /^(iPad|iPhone|default)$/;

    for (const element of clonedResult.data) {
      const valueToValidate = element[index];
      if (!userDeviceRegex.test(valueToValidate)) {
        setValidationError(`Unsupported user device value: ${valueToValidate}`);
        return false;
      }
    }

    return true;
  };

  const validateRegion = (
    results: any,
    geoLocation: string,
    geoLocationType: string
  ): boolean => {
    const clonedResult = cloneDeep(results);
    const index = clonedResult.data[0].indexOf(geoLocation);
    clonedResult.data.shift();

    if (geoLocationType === "dma") {
      for (const element of clonedResult.data) {
        if (!DMA_LIST.map((item) => item.name).includes(element[index])) {
          setValidationError(`Unsupported DMA value: ${element[index]}`);
          return false;
        }
      }
    } else {
      for (const element of clonedResult.data) {
        if (!REGIONS_LIST.map((item) => item.name).includes(element[index])) {
          setValidationError(`Unsupported Region value: ${element[index]}`);
          return false;
        }
      }
    }

    return true;
  };

  const handleSubmit = async (values: StepTwoData) => {
    setLoading(true);
    setValidationError(null);
    setValidationWarnings([]);

    try {
      // Validate the data in the CSV file
      Papa.parse(dataFile, {
        complete: (results) => {
          let isValid = true;
          const warnings: string[] = [];

          // Validate required fields
          if (!values.userId || !values.targetVariable) {
            setValidationError(
              "Customer Identifier and Target Variable are required"
            );
            setLoading(false);
            return;
          }

          // Check for recommended fields
          if (
            !values.age &&
            !values.gender &&
            !values.locale &&
            !values.geoLocation
          ) {
            warnings.push(
              "No demographic or geographic variables selected. This may reduce optimization effectiveness."
            );
          }

          // Validate optional fields if they are provided
          if (values.geoLocation && values.geoLocationType) {
            isValid = validateRegion(
              results,
              values.geoLocation,
              values.geoLocationType
            );
            if (!isValid) {
              setLoading(false);
              return;
            }
          } else if (values.geoLocation && !values.geoLocationType) {
            setValidationError(
              "Please select a Geo Location Type when Geo Location is provided"
            );
            setLoading(false);
            return;
          }

          if (values.age) {
            isValid = validateAge(results, values.age);
            if (!isValid) {
              setLoading(false);
              return;
            }
          }

          if (values.locale) {
            isValid = validateLocale(results, values.locale);
            if (!isValid) {
              setLoading(false);
              return;
            }
          }

          if (values.gender) {
            isValid = validateGender(results, values.gender);
            if (!isValid) {
              setLoading(false);
              return;
            }
          }

          if (values.devicePlatform) {
            isValid = validateDevicePlatform(results, values.devicePlatform);
            if (!isValid) {
              setLoading(false);
              return;
            }
          }

          if (values.userOs) {
            isValid = validateUserOs(results, values.userOs);
            if (!isValid) {
              setLoading(false);
              return;
            }
          }

          if (values.userDevice) {
            isValid = validateUserDevice(results, values.userDevice);
            if (!isValid) {
              setLoading(false);
              return;
            }
          }

          setValidationWarnings(warnings);

          // If all validations pass, proceed to next step
          if (isValid) {
            onNext(values);
          }

          setLoading(false);
        },
        error: (error) => {
          console.error("Error parsing CSV:", error);
          setValidationError("Error parsing CSV file");
          setLoading(false);
        },
      });
    } catch (error) {
      console.error("Error:", error);
      setValidationError("An unexpected error occurred");
      setLoading(false);
    }
  };

  // Generate table columns from headers with highlighting for selected columns
  const tableColumns = headers.map((header) => ({
    title: (
      <div>
        {header}
        {selectedColumns[header] && (
          <Badge
            count={
              <Tooltip title={`Selected as ${selectedColumns[header]}`}>
                <CheckCircleOutlined style={{ color: "#52c41a" }} />
              </Tooltip>
            }
            offset={[5, 0]}
          />
        )}
      </div>
    ),
    dataIndex: header,
    key: header,
    ellipsis: true,
    className: selectedColumns[header] ? "bg-green-50" : "",
  }));

  return (
    <div className="space-y-6">
      <Card className="shadow-sm">
        <Title level={4}>Configure Bid Optimizer</Title>
        <Paragraph className="text-gray-600 mb-6">
          Select the variables from your data that will be used for bid
          optimization. Required fields are marked with an asterisk (*).
        </Paragraph>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{}} // Initial values are set in useEffect
          onValuesChange={(changedValues, allValues) => {
            // Update selected columns when form values change
            const selected: Record<string, string> = {};

            Object.entries(allValues).forEach(([key, value]) => {
              if (value && headers.includes(value as string)) {
                selected[value as string] = key;
              }
            });

            setSelectedColumns(selected);

            if (changedValues.name !== undefined) {
              onNameChange?.(changedValues.name);
            }
          }}
        >
          <Card
            title={
              <div className="flex items-center">
                <span className="text-lg font-medium">Basic Information</span>
              </div>
            }
            className="mb-6 shadow-sm"
            bordered={false}
          >
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="name"
                  label="Job Name"
                  rules={[
                    { required: true, message: "Please enter a name" },
                    { max: 100, message: "Name cannot exceed 100 characters" },
                  ]}
                  tooltip={{
                    title: "Name of the Job to identify in Jobs list",
                    icon: <InfoCircleOutlined />,
                  }}
                >
                  <Input placeholder="Enter job name" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="campaignObjective"
                  label="Campaign Objective"
                  rules={[
                    { required: true, message: "Please select an objective" },
                  ]}
                  tooltip={{
                    title:
                      "Objective of the campaign for better reach and cost optimization",
                    icon: <InfoCircleOutlined />,
                  }}
                >
                  <Select options={campaignObjectiveOptions} />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card
            title={
              <div className="flex items-center">
                <span className="text-lg font-medium">Required Variables</span>
                <Badge
                  count="Required"
                  style={{ backgroundColor: "#1890ff" }}
                  className="ml-2"
                />
              </div>
            }
            className="mb-6 shadow-sm"
            bordered={false}
          >
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="userId"
                  label="Customer Identifier"
                  rules={[
                    {
                      required: true,
                      message: "Please select Customer Id variable",
                    },
                  ]}
                  tooltip={{
                    title:
                      "Customer Id for unique identification of records. Eg: Id or email",
                    icon: <InfoCircleOutlined />,
                  }}
                >
                  <Select
                    placeholder="Select column"
                    showSearch
                    optionFilterProp="children"
                  >
                    {headers.map((header) => (
                      <Select.Option key={header} value={header}>
                        {header}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="targetVariable"
                  label="Target Variable"
                  rules={[
                    {
                      required: true,
                      message: "Please select Target variable",
                    },
                  ]}
                  tooltip={{
                    title:
                      "Column name that model can process against dependent variables. Eg: Sales",
                    icon: <InfoCircleOutlined />,
                  }}
                >
                  <Select
                    placeholder="Select column"
                    showSearch
                    optionFilterProp="children"
                  >
                    {headers.map((header) => (
                      <Select.Option key={header} value={header}>
                        {header}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card
            title={
              <div className="flex items-center">
                <span className="text-lg font-medium">
                  Demographic Variables
                </span>
                <Badge
                  count="Recommended"
                  style={{ backgroundColor: "#52c41a" }}
                  className="ml-2"
                />
              </div>
            }
            className="mb-6 shadow-sm"
            bordered={false}
          >
            <Row gutter={24}>
              <Col span={8}>
                <Form.Item
                  name="age"
                  label="Age Range"
                  tooltip={{
                    title:
                      "Column name that has Age range for a record in your CSV file. Values should be in format '18-24', '25-34', etc.",
                    icon: <InfoCircleOutlined />,
                  }}
                >
                  <Select
                    placeholder="Select column"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                  >
                    {headers.map((header) => (
                      <Select.Option key={header} value={header}>
                        {header}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="gender"
                  label="Gender"
                  tooltip={{
                    title:
                      "Column name that has gender for a record in your CSV file. Values should be 'male', 'female', or 'default'",
                    icon: <InfoCircleOutlined />,
                  }}
                >
                  <Select
                    placeholder="Select column"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                  >
                    {headers.map((header) => (
                      <Select.Option key={header} value={header}>
                        {header}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="locale"
                  label="Locale"
                  tooltip={{
                    title:
                      "Column name that has Locale for a record in your CSV file",
                    icon: <InfoCircleOutlined />,
                  }}
                >
                  <Select
                    placeholder="Select column"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                  >
                    {headers.map((header) => (
                      <Select.Option key={header} value={header}>
                        {header}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card
            title={
              <div className="flex items-center">
                <span className="text-lg font-medium">
                  Geographic Variables
                </span>
                <Badge
                  count="Recommended"
                  style={{ backgroundColor: "#52c41a" }}
                  className="ml-2"
                />
              </div>
            }
            className="mb-6 shadow-sm"
            bordered={false}
          >
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="geoLocationType"
                  label="Geo Location Type"
                  tooltip={{
                    title: "Select the geo location type",
                    icon: <InfoCircleOutlined />,
                  }}
                >
                  <Select
                    placeholder="Select type"
                    allowClear
                    options={geoLocationTypeOptions}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="geoLocation"
                  label="Geo Location"
                  tooltip={{
                    title:
                      "Column name that has Geo Location for a record in your CSV file",
                    icon: <InfoCircleOutlined />,
                  }}
                >
                  <Select
                    placeholder="Select column"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                  >
                    {headers.map((header) => (
                      <Select.Option key={header} value={header}>
                        {header}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card
            title={
              <div className="flex items-center">
                <span className="text-lg font-medium">Device Variables</span>
                <Badge
                  count="Optional"
                  style={{ backgroundColor: "#faad14" }}
                  className="ml-2"
                />
              </div>
            }
            className="mb-6 shadow-sm"
            bordered={false}
          >
            <Row gutter={24}>
              <Col span={8}>
                <Form.Item
                  name="devicePlatform"
                  label="Device Platform"
                  tooltip={{
                    title:
                      "Column name that has Device Platform for a record in your CSV file. Values should be 'mobile' or 'desktop'",
                    icon: <InfoCircleOutlined />,
                  }}
                >
                  <Select
                    placeholder="Select column"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                  >
                    {headers.map((header) => (
                      <Select.Option key={header} value={header}>
                        {header}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="userOs"
                  label="User OS"
                  tooltip={{
                    title:
                      "Column name that has User OS for a record in your CSV file. Values should be 'Android', 'Windows', 'iOS', 'Windows Phone', or 'default'",
                    icon: <InfoCircleOutlined />,
                  }}
                >
                  <Select
                    placeholder="Select column"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                  >
                    {headers.map((header) => (
                      <Select.Option key={header} value={header}>
                        {header}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="userDevice"
                  label="User Device"
                  tooltip={{
                    title:
                      "Column name that has User Device for a record in your CSV file. Values should be 'iPad', 'iPhone', or 'default'",
                    icon: <InfoCircleOutlined />,
                  }}
                >
                  <Select
                    placeholder="Select column"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                  >
                    {headers.map((header) => (
                      <Select.Option key={header} value={header}>
                        {header}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Data Preview with highlighted selected columns */}
          <Card
            title="Data Preview"
            className="mb-6 shadow-sm"
            bordered={false}
            extra={
              <Text type="secondary">Selected columns are highlighted</Text>
            }
          >
            <div className="max-h-[250px] overflow-auto">
              <Table
                dataSource={previewData}
                columns={tableColumns}
                size="small"
                pagination={false}
                bordered
                scroll={{ x: "max-content" }}
                rowKey={(record, index) => index?.toString() || "0"}
              />
            </div>
          </Card>

          {validationError && (
            <Alert
              message="Validation Error"
              description={validationError}
              type="error"
              showIcon
              icon={<WarningOutlined />}
              className="mb-6"
            />
          )}

          {validationWarnings.length > 0 && (
            <Alert
              message="Recommendations"
              description={
                <ul className="list-disc pl-5">
                  {validationWarnings.map((warning, index) => (
                    <li key={index}>{warning}</li>
                  ))}
                </ul>
              }
              type="warning"
              showIcon
              className="mb-6"
            />
          )}

          <div className="flex justify-between">
            <Button size="large" onClick={onPrevious} disabled={loading}>
              Previous
            </Button>
            <Button
              type="primary"
              size="large"
              htmlType="submit"
              loading={loading}
            >
              Next
            </Button>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default StepTwo;
